/**
 * Decide qual painel de especialidade renderiza um registro do prontuario.
 *
 * Duas perguntas diferentes, que antes estavam confundidas em duas copias de
 * shouldUseOphthalmologyPanel:
 *
 *   - quais campos existem  -> provisionamento, decidido pelo PROFISSIONAL
 *   - como isto renderiza   -> layout, decidido pelo AGENDAMENTO
 *
 * O layout vai pelo agendamento porque um profissional pode ter mais de uma
 * especialidade: o que importa e para que aquele atendimento foi marcado.
 *
 * Devolve uma chave, nao um componente, para ser testavel sem montar Vue.
 */

import { visibleAttributes } from './record-attributes.js';

export const PANEL_ODONTOLOGY = 'odontology';
export const PANEL_OPHTHALMOLOGY = 'ophthalmology';

/**
 * Tipos que nao cabem na coluna estreita do Splitter e precisam da largura
 * inteira. Um odontograma tem 32 dentes com 5 faces cada: em 40% da tela cada
 * face fica com poucos pixels. O painel pre-natal tem a grade da consulta e
 * uma linha por feto na gestacao multipla.
 */
export const FULL_WIDTH_TYPES = ['odontogram', 'prenatal_panel'];

/**
 * Separa os atributos que vao na faixa de largura total dos que continuam na
 * coluna do Splitter. Campos removidos do registro ficam de fora das duas.
 *
 * @param {Array} attributes
 * @returns {{band: Array, rest: Array}}
 */
export function splitFullWidthAttributes(attributes = []) {
  const band = [];
  const rest = [];

  for (const attribute of visibleAttributes(attributes)) {
    (FULL_WIDTH_TYPES.includes(attribute.type) ? band : rest).push(attribute);
  }

  return { band, rest };
}
/**
 * @param {object|null} schedule       agendamento em curso (pode ser sintetico)
 * @param {object|null} medicalRecord  registro sendo renderizado
 * @param {Array} recordTypes          tipos de registro carregados na tela
 * @returns {string|null}
 */
export function resolveSpecialtyPanel(schedule, medicalRecord, recordTypes = []) {
  const attributes = medicalRecord?.attributes ?? [];

  // Odontologia casa pelo kind, nunca pelo nome: Dentistica, Endodontia,
  // Ortodontia e Periodontia nao tem "odonto" no nome.
  if (schedule?.specialty?.kind?.kind_acronym === 'CRO') {
    return PANEL_ODONTOLOGY;
  }

  // Registro avulso: /patients/:patientId monta um schedule sintetico sem
  // especialidade, entao o proprio conteudo do registro decide.
  if (attributes.some((attribute) => attribute.type === 'odontogram')) {
    return PANEL_ODONTOLOGY;
  }

  // Oftalmologia: comportamento legado preservado tal como estava.
  const specialtyName = schedule?.specialty?.spec_name?.toLowerCase?.() ?? '';
  if (!specialtyName.includes('oftalmologia')) {
    return null;
  }

  const isConsulta = (medicalRecord?.record_type?.rectype_name ?? '').toLowerCase() === 'consulta';
  if (!isConsulta) {
    return null;
  }

  const hasImageAnnotation = attributes.some((attribute) => attribute.type === 'image_annotation');
  const recordType = (recordTypes ?? []).find((item) => item.id_rectype === medicalRecord?.id_rectype);

  return (recordType?.use_specialty_panel || hasImageAnnotation) ? PANEL_OPHTHALMOLOGY : null;
}
